import { Injectable } from '@angular/core';
import { config } from 'config';
import { LoaderService } from './loader.service';
import { Router } from '@angular/router';
import { appSetting } from 'config/appSettings';
import { customAlphabet } from 'nanoid';
import { AuthService } from './auth.service';
import { UserDataService } from './user-data.service';

@Injectable()
export class AppHelperService {

    public pluralMapping = {
        '=0': '.NONE',
        '=1': '.SINGULAR',
        'other': '.PLURAL'
    };

    /**
     * This function will generate and return nanoid without underscore and length 16 by default
     */
    public generateNanoId = customAlphabet('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz', 16);
    
    constructor(
        private loaderService: LoaderService,
		private userDataService: UserDataService,
        private router : Router
    ) { }
    
    isAbsoluteUrl(url) {
        let pat = /(^https?:\/\/)|(^\/\/)/i;
        return pat.test(url);
    }
    
    // This method navigates the user to the dashboard. Pass the correct 'urlArray' based on the user's role. 
    // If the role or tab information is unavailable, pass an empty array. The method will handle the navigation accordingly.
    navigateToDashboardApp(urlArray, options?) {
        urlArray = urlArray.length > 0 ? urlArray : this._getUrlArrayBasedOnRole();
        this.navigateToApp(config.dashboardAppDomain, urlArray, options);
    }
    
    // Updates the URL based on the user's role and last visited tab.
    // If the user is a teacher, it retrieves the last visited tab from local storage.
    // If the last visited tab is available, it sets the tab to that value.
    // Returns an array with the user's role and the selected tab.
    _getUrlArrayBasedOnRole() {
        const userData = this.userDataService.getUserData();
        let role = userData.role;
        // Default tab
        let tab = 'dashboard';

        if (role === 'teacher') {
            // Generate a key for local storage using the user's external ID (extUserId)
            const key = appSetting.localStorage.dashboard(userData.extUserId);

            // Retrieve the last visited tab data from localStorage using the key
            const lastVisitedTab = localStorage.getItem(key);

            // If last visited tab data exists and contains a valid selectedTab, update the tab to selectedTab
            if (lastVisitedTab) {
                const { selectedTab } = JSON.parse(lastVisitedTab);
                if (selectedTab) {
                    tab = selectedTab;
                }
            }
        }
        else if (role == 'student') {
            role = 'learner';
        }

        return [role, tab];
    }

    navigateToOnboardingApp(urlArray, options?) {
        this.navigateToApp(config.onBoardingAppDomain, urlArray, options);
    }

    navigateToClassApp(urlArray, options?) {
        this.navigateToApp(config.classAppDomain, urlArray, options);
    }

    /**
     * this function has been created to take the user back to FOC 
     * when class app was launched as part of Assignment creation flow
     * from inside the FOC
     * @param ebookAssignmentFlowParams 
     */
    navigateToFOC(ebookAssignmentFlowParams) {
        let cId = ebookAssignmentFlowParams.classId;
        let bundleId = ebookAssignmentFlowParams.bundleId;
        let bookId = ebookAssignmentFlowParams.bookId;
        let spaceId = ebookAssignmentFlowParams.spaceId;
        let currentPageLabel = 'view?page=' + ebookAssignmentFlowParams.currentPageLabel;

        let urlArray;
        if (cId == "undefined") {
            urlArray = [spaceId, 'product', bundleId, 'studentbook', bookId, currentPageLabel];
        } else {
            urlArray = [spaceId, 'class', cId, 'product', bundleId, 'studentbook', bookId, currentPageLabel];
        }
        this.navigateToApp(config.focAppDomain, urlArray);
    }

    navigateToAdminApp(urlArray, options?) {
        this.navigateToApp(config.adminAppDomain, urlArray, options);
    }

    // Method to return selected tab selected by user based on local storage
    _getSelectedTab(userId){
        let tab = 'dashboard';
        // Generate a key for local storage using the user's external ID (extUserId)
        const key = appSetting.localStorage.dashboard(userId);
            
        // Retrieve the last visited tab data from localStorage using the key
        const lastVisitedTab = localStorage.getItem(key);
        
        // If last visited tab data exists and contains a valid selectedTab, update the tab to selectedTab
        if (lastVisitedTab) {
            const { selectedTab } = JSON.parse(lastVisitedTab);
            if (selectedTab) {
                tab = selectedTab;
            }
        }
        return tab;
    }
    
    showLoaderAndNavigateToDashboard(userData, options: any = {}) {
        if (this.router.url.indexOf('/dashboard') === -1) {

            // Get the value from localstorage the current toggle value for admin is "admin/teacher"
            let key = appSetting.localStorage.adminSelectedView + userData.extUserId;
            let adminSelectedView = localStorage.getItem(key);
            let urlArray = [];

            // if user id admin as toggled to teacher navigate to teacher Dashboard
            // else the '/' route will take to correct location
            if (userData.role == 'admin') {
                if(adminSelectedView === "teacher") {
                    const selectedTab = this._getSelectedTab(userData.extUserId);
                    urlArray.push(`teacher/${selectedTab}`);
                } else {
                    urlArray.push('admin/dashboard');
                }
            }

            urlArray = urlArray.length > 0 ? urlArray : this._getUrlArrayBasedOnRole();
            this.navigateToApp(config.dashboardAppDomain, urlArray, options);
        }
    }

    public navigateToApp(domain, pathParams, options: any = {}) {
        this.loaderService.showOverlayLoader();
        if (options && options.outsideC1Url && this.isAbsoluteUrl(options.outsideC1Url)) {
            if (options.openInNewTab) {
                this.loaderService.hideLoader();
                window.open(options.outsideC1Url, "_blank");
                return;
            }
            return this.router.navigate(['/externalRedirect', { externalUrl: options.outsideC1Url }], {
                skipLocationChange: true,
            });
        }
        let externalUrl = domain + pathParams.join('/');
        if (options && options.queryParams && Object.keys(options.queryParams).length > 0) {
            externalUrl += '?' + Object.keys(options.queryParams).map(key => `${key}=${options.queryParams[key]}`).join('&');
        }
        if (options && options.hashParams && Object.keys(options.hashParams).length > 0) {
            externalUrl += '#' + Object.keys(options.hashParams).map(key => `${key}=${options.hashParams[key]}`).join('&');
        }
        if (options && options.forceExit) {
            window.location.href = externalUrl;
        } else {
            this.router.navigate(['/externalRedirect', { externalUrl }], {
                skipLocationChange: true,
                state: options?.state
            });
        }
    }

    // This function is used to set banner data in local storage.
    setBannerDataInLocalStorage(bannerData, extUserId) {
        const key = `${appSetting.localStorage.messageBanner}${extUserId}`;
        localStorage.setItem(key, JSON.stringify(bannerData));
    }

    // This function returns banner data from the local storage.
    getBannerDataFromLocalStorage(extUserId) {
        const key = `${appSetting.localStorage.messageBanner}${extUserId}`;
        const localStorageMsgBannerData = localStorage.getItem(key);
        if (localStorageMsgBannerData) {
            const msgBannerData = JSON.parse(localStorageMsgBannerData);
            localStorage.removeItem(key);
            return msgBannerData;
        }
    }

    /*
        * This function is used for getting required query params from existing query params in url
    */
    getRequiredQueryParams(parmsObject, requiredFields) {
        let mappedQuery = {};
        requiredFields.forEach(param=>{
            if(parmsObject.hasOwnProperty(param)) {
                mappedQuery[param] = parmsObject[param];
            }
        })
        return mappedQuery;
    }

     // Get folder code from itemid
    getLastFolderCodeFromItemId(itemId) {
        const itemCodes = itemId.split('/');
        if (itemCodes.length > 1) {
            itemCodes.pop();
            return itemCodes.join('/');
        }
        else {
            return '';
        }
    }

    isSafariUA() {
        let userAgent = navigator && navigator.userAgent;
        if (!userAgent) {
            return false;
        }
        userAgent = userAgent.toLowerCase();
        const chromeAgent = userAgent.includes('chrome');
        // Including CriOS and FxiOS check to exclude chrome and firefox in iOS
        // issue: https://github.com/comprodls/nemo-micro-learning-path/issues/1636
        // reference: https://stackoverflow.com/a/31732310/9879667
        const chromeAgentInIOS = userAgent.includes('crios');
        const firefoxAgentInIOS = userAgent.includes('fxios');
        let safariAgent = userAgent.includes('safari');

        // Discard Safari since it also matches Chrome/Firefox
        if ((chromeAgent || chromeAgentInIOS || firefoxAgentInIOS) && (safariAgent)) safariAgent = false;

        return safariAgent;
    }

    checkMQAUsingDlsOrgId(dlsOrgId) {
        return dlsOrgId && dlsOrgId.indexOf("mqa") === 0;
    }

    getCookie(name) {
        let match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        if (match) return match[2];
    }

    isProductReviewMode() {
        return window.location.href && (window.location.href.indexOf('/review-assignment/') !== -1 || window.location.href.indexOf('/cefr-review/') !== -1);
    }

    getLastTimeStampForDate(timestamp) {
        const date = new Date(timestamp);
        date.setHours(23); 
        date.setMinutes(59);
        date.setSeconds(59);
        date.setMilliseconds(999);
        return date.getTime();
    }

    isMosaicProduct(code) {
        for (const bundleCode in config.mosaicProductsClassification) {
            for (const productCode in config.mosaicProductsClassification[bundleCode]) {
                if (productCode === code) {
                    return true;
                }
            }
        }
        return false;
    }
}
